import React, { useCallback, useEffect, useRef } from 'react';
import Hls from 'hls.js';
import type { ErrorData, HlsConfig } from 'hls.js';
import { toast } from '@/components';
import { createContinuationLoader } from '@/utils/hls-continuation-loader';
import { ViinexHlsLoader } from '@/services/viinex-hls-loader';
import {
    MEDIA_ERROR_TOAST_ID,
    translateMediaError,
} from '@/utils/error-messages';
import type { VideoPlayerEventEmitter } from './VideoPlayerEventEmitter';
import { useVideoPlayerContext } from './VideoPlayerProvider';

const BACK_BUFFER_LENGTH_SECONDS = 30;
const NETWORK_RECOVERY_DELAY_MS = 1000;

const BROWSER_CODEC_ERROR_DETAILS = new Set<string>([
    Hls.ErrorDetails.MANIFEST_INCOMPATIBLE_CODECS_ERROR,
    Hls.ErrorDetails.BUFFER_ADD_CODEC_ERROR,
    Hls.ErrorDetails.BUFFER_INCOMPATIBLE_CODECS_ERROR,
]);

function isBrowserCodecError(error: ErrorData): boolean {
    return error.fatal && BROWSER_CODEC_ERROR_DETAILS.has(error.details);
}

function reportUnsupportedBrowser(
    emitter: VideoPlayerEventEmitter,
    messageKey: string,
): void {
    emitter.emit('error', new Error(messageKey));
    toast.error(translateMediaError(messageKey), {
        id: MEDIA_ERROR_TOAST_ID,
    });
}

function parseCameraStreamUrl(videoSrc: string): {
    baseUrl: string;
    beginMs: number;
} | null {
    try {
        const url = new URL(videoSrc, window.location.origin);
        const beginMs = parseInt(url.searchParams.get('begin') || '', 10);
        if (isNaN(beginMs)) return null;
        const baseUrl = videoSrc.split('/stream.m3u8')[0];
        return { baseUrl, beginMs };
    } catch {
        return null;
    }
}

export function CameraHlsVideo() {
    const { mediaRef, streaming, playing, playbackRate, onError, emitter } =
        useVideoPlayerContext();

    const hlsRef = useRef<Hls | null>(null);
    const videoRef = useRef<HTMLVideoElement | null>(null);

    const setVideoRef = useCallback(
        (node: HTMLVideoElement | null) => {
            videoRef.current = node;
            (
                mediaRef as React.MutableRefObject<HTMLVideoElement | null>
            ).current = node;
        },
        [mediaRef],
    );

    useEffect(() => {
        const video = videoRef.current;
        if (!video || !streaming.videoSrc) return;

        if (!Hls.isSupported()) {
            reportUnsupportedBrowser(emitter, 'streamingUnsupported');
            return;
        }

        const parsed = parseCameraStreamUrl(streaming.videoSrc);
        if (!parsed) return;

        const config: Partial<HlsConfig> = {
            enableWorker: true,
            lowLatencyMode: false,
            liveDurationInfinity: true,
            backBufferLength: BACK_BUFFER_LENGTH_SECONDS,
            levelLoadingMaxRetry: 10,
            manifestLoadingMaxRetry: 10,
            liveSyncDuration: 120,
            loader: ViinexHlsLoader,
            pLoader: createContinuationLoader(parsed.baseUrl, parsed.beginMs),
        };

        const hls = new Hls(config);
        let networkRecoveryTimer: ReturnType<typeof setTimeout> | null = null;
        let failedRequestUrls: (string | undefined)[] = [];
        const cancelNetworkRecovery = () => {
            if (networkRecoveryTimer !== null)
                clearTimeout(networkRecoveryTimer);
            networkRecoveryTimer = null;
        };
        const recoverRequest = (url: string | undefined) => {
            if (!url) return;
            failedRequestUrls = failedRequestUrls.filter(
                (failedUrl) => failedUrl !== url,
            );
            if (failedRequestUrls.length === 0) cancelNetworkRecovery();
        };
        hlsRef.current = hls;
        (video as any).api = hls;

        hls.loadSource(streaming.videoSrc);
        hls.attachMedia(video);

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
            emitter.emit('ready');
            if (playing) video.play();
        });

        hls.on(Hls.Events.MANIFEST_LOADED, (_, data) =>
            recoverRequest(data.url),
        );
        hls.on(Hls.Events.LEVEL_LOADED, (_, data) =>
            recoverRequest(data.details.url),
        );
        hls.on(Hls.Events.FRAG_LOADED, (_, data) =>
            recoverRequest(data.frag.url),
        );

        hls.on(Hls.Events.ERROR, (_, data) => {
            if (isBrowserCodecError(data)) {
                reportUnsupportedBrowser(emitter, 'streamCodecUnsupported');
                return;
            }
            if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
                if (data.response?.code === 401) {
                    cancelNetworkRecovery();
                    hls.stopLoad();
                    emitter.emit('error', data.error);
                    return;
                }
                const failedUrl =
                    data.url ?? data.frag?.url ?? data.context?.url;
                if (!failedRequestUrls.includes(failedUrl)) {
                    failedRequestUrls = [...failedRequestUrls, failedUrl];
                }
                if (networkRecoveryTimer === null) {
                    // Let HLS finish handling the failed request before restarting its controllers.
                    networkRecoveryTimer = setTimeout(() => {
                        networkRecoveryTimer = null;
                        failedRequestUrls = [];
                        hls.startLoad();
                    }, NETWORK_RECOVERY_DELAY_MS);
                }
            } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            } else if (data.fatal) {
                onError(data);
                return;
            }
            if (playing) {
                video.addEventListener('canplay', () => video.play(), {
                    once: true,
                });
            }
        });

        return () => {
            cancelNetworkRecovery();
            hls.destroy();
            hlsRef.current = null;
            (video as any).api = null;
        };
    }, [streaming.videoSrc]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        if (playing) {
            video.play();
        } else {
            video.pause();
        }
    }, [playing]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        video.playbackRate = playbackRate;
    }, [playbackRate]);

    if (!streaming.videoSrc) return null;

    return (
        <video
            ref={setVideoRef}
            className="rounded-lg overflow-hidden object-contain max-h-none w-full h-full"
            crossOrigin="use-credentials"
            muted
            onDurationChange={() => emitter.emit('durationchange')}
            onEnded={() => emitter.emit('ended')}
            onLoadedData={() => emitter.emit('loadeddata')}
            onLoadedMetadata={() => emitter.emit('loadedmetadata')}
            onPause={() => emitter.emit('pause')}
            onPlay={() => emitter.emit('play')}
            onPlaying={() => emitter.emit('playing')}
            onRateChange={() => emitter.emit('ratechange')}
            onSeeked={() => emitter.emit('seeked')}
            onSeeking={() => emitter.emit('seeking')}
            onTimeUpdate={() => emitter.emit('timeupdate')}
            onWaiting={() => emitter.emit('waiting')}
        />
    );
}
